import { useState } from "react";
import { Link } from "react-router-dom";
import { Plus, ArrowUpRight } from "lucide-react";
import { useCart } from "@/lib/cart";
import { useProducts, slug } from "@/lib/products";
import VialImage from "@/components/VialImage";
import Reveal from "@/components/Reveal";
import { Button } from "@/components/ui/button";

const FEATURED = ["BPC-157", "GLP-3 (R)", "GHK-Cu", "Wolverine Stack", "BBG70 Glow Blend", "Performance Stack"];

function FeaturedCard({ name, sizes, i }) {
  const { add } = useCart();
  const [sel, setSel] = useState(sizes[0]);
  const from = Math.min(...sizes.map((s) => s.retail_price));
  return (
    <Reveal delay={(i % 3) * 0.08}>
      <div className="group grid grid-cols-[140px_1fr] sm:grid-cols-[170px_1fr] border border-slate-200 bg-white hover:border-sky-400 transition-colors h-full"
        data-testid={`featured-sku-card-${slug(name)}`}>
        <div className="relative flex items-center justify-center border-r border-slate-100 bg-white p-3">
          <VialImage product={sel} className="h-52 sm:h-60 transition-transform duration-500 group-hover:-translate-y-1" />
          {sel.coa_lot && (
            <span className="absolute top-2 left-2 font-tech text-[9px] uppercase tracking-[0.14em] text-slate-500 border border-slate-200 bg-white px-1.5 py-0.5">
              Lot {sel.coa_lot}
            </span>
          )}
        </div>
        <div className="p-5 flex flex-col">
          <p className="font-tech text-[10px] uppercase tracking-[0.18em] text-sky-700">{sel.category}</p>
          <h3 className="font-display text-xl font-semibold text-slate-900 mt-1 leading-tight">{name}</h3>
          {sel.alias && <p className="text-xs text-slate-500 mt-0.5">{sel.alias}</p>}
          <div className="flex flex-wrap gap-1.5 mt-4">
            {sizes.map((s) => (
              <button key={s.id} onClick={() => setSel(s)} data-testid={`featured-size-${s.id}`}
                className={`font-tech text-[11px] px-2.5 py-1 border rounded-full transition-colors ${
                  sel.id === s.id ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-300 hover:border-slate-500"}`}>
                {s.specification.split(" ")[0]}
              </button>
            ))}
          </div>
          <div className="mt-auto pt-5 flex items-end justify-between gap-3">
            <div>
              <p className="font-tech text-[10px] uppercase tracking-wide text-slate-400">{sel.id === sizes[0].id ? "Starting at" : "Selected"}</p>
              <p className="font-tech text-xl font-semibold text-slate-900">${(sel.id === sizes[0].id ? from : sel.retail_price).toFixed(2)}</p>
            </div>
            <Button size="sm" onClick={() => add(sel)} data-testid={`featured-add-btn-${sel.id}`} className="gap-1">
              <Plus className="h-3.5 w-3.5" /> Add
            </Button>
          </div>
        </div>
      </div>
    </Reveal>
  );
}

export default function FeaturedSkus() {
  const products = useProducts();
  const groups = FEATURED.map((n) => ({ name: n, sizes: products.filter((p) => p.name === n).sort((a, b) => a.retail_price - b.retail_price) })).filter((g) => g.sizes.length);

  return (
    <section id="featured" className="max-w-7xl mx-auto px-4 sm:px-6 py-20 sm:py-28" data-testid="featured-section">
      <Reveal className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-10">
        <div>
          <p className="font-tech text-xs uppercase tracking-[0.18em] text-sky-700 mb-3">Catalog records</p>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-slate-900 leading-[1.05]">Active catalog SKUs.</h2>
          <p className="mt-4 text-base text-slate-600 leading-relaxed max-w-xl">
            Pick a size, add it to your order request. Every record is labeled by compound, strength and testing status so you can compare quickly.
          </p>
        </div>
        <Link to="/catalog" data-testid="featured-view-catalog-link"
          className="inline-flex items-center gap-2 bg-slate-900 hover:bg-sky-700 text-white font-medium text-sm px-5 py-3 rounded-full transition-colors whitespace-nowrap">
          Research catalog <ArrowUpRight className="h-4 w-4" />
        </Link>
      </Reveal>
      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-5">
        {groups.map((g, i) => <FeaturedCard key={g.name} {...g} i={i} />)}
      </div>
    </section>
  );
}
